<template>
  <div class="page stack">
    <!-- The week is planned: the nights, and the way in. -->
    <section v-if="planned.length" class="card hero">
      <p class="eyebrow">The week of {{ dayLabel(week.days[0]) }} · {{ planned.length === 1 ? 'one lesson' : `${planned.length} lessons` }}</p>
      <div v-for="{ lesson, iso } in planned" :key="lesson.id" class="night" :class="{ 'night--done': progress[lesson.id]?.cookedAt, 'night--next': next && next.id === lesson.id }">
        <span class="night__day">{{ weekdayLabel(iso) }}</span>
        <span class="night__text">
          <router-link :to="`/lesson/${lesson.id}`" class="night__title">{{ lesson.number }}. {{ lesson.title }}</router-link>
          <span class="muted small">{{ lesson.dish }} · {{ lesson.side }}</span>
        </span>
        <router-link v-if="progress[lesson.id]?.cookedAt && !progress[lesson.id]?.debrief" :to="`/debrief/${lesson.id}`" class="btn btn-sm">We ate. Talk.</router-link>
        <span v-else-if="progress[lesson.id]?.cookedAt" class="pill pill--green">cooked</span>
        <router-link v-else-if="progress[lesson.id]?.startedAt" :to="`/cook/${lesson.id}`" class="btn btn-sm btn-primary">Keep cooking</router-link>
      </div>
      <div class="row hero__actions">
        <router-link v-if="next" :to="`/cook/${next.id}`" class="btn btn-primary btn-lg">It’s time to cook: {{ next.title }}</router-link>
        <router-link to="/week" class="btn btn-lg">{{ weekEntry?.orderedAt ? 'The order ✓' : 'Order & groceries' }}</router-link>
      </div>
      <p v-if="!weekEntry?.orderedAt" class="muted small">Creamery order by {{ dayLabel(week.orderBy) }}.</p>
    </section>

    <!-- Nothing planned yet: the question. -->
    <section v-else-if="remaining.length" class="card hero">
      <p class="eyebrow">The week of {{ dayLabel(week.days[0]) }} · order by {{ dayLabel(week.orderBy) }}</p>
      <h1 class="hero__title">How many lessons this week?</h1>
      <p class="muted">Up next: {{ remaining[0].title }}, {{ remaining[0].dish.toLowerCase() }}. Pick a number and the next lessons fill the week.</p>
      <div class="row">
        <router-link v-for="n in countOptions" :key="n" :to="`/week?count=${n}`" class="btn btn-lg count-btn" :class="{ 'btn-primary': n === 2 }">{{ n }}</router-link>
      </div>
    </section>

    <section v-else class="card">
      <p class="eyebrow">The course</p>
      <h1>All eight lessons cooked.</h1>
      <p class="muted">That was the curriculum. Cook any of them again from the list below, or say what you would like to learn next.</p>
    </section>

    <section v-if="lastDebrief" class="card">
      <p class="eyebrow">From last time · {{ lastDebrief.title }}</p>
      <p class="debrief-verdict">{{ lastDebrief.summary.verdict }}</p>
      <p v-if="lastDebrief.summary.nextTime" class="muted">Next time: {{ lastDebrief.summary.nextTime }}</p>
    </section>

    <section class="card week">
      <p class="eyebrow">How a week runs</p>
      <ol class="week__list">
        <li><strong>By Monday</strong> — say how many lessons, pick the nights, add the creamery items to the South Mountain order, and send the rest to the Meal Hat list.</li>
        <li><strong>Thursday</strong> — delivery arrives. The nights are on Meal Hat’s schedule.</li>
        <li><strong>Each lesson night</strong> — an hour before dinner, “It’s time to cook”.</li>
        <li><strong>After dinner</strong> — one button, everyone talks, the next lesson listens.</li>
      </ol>
    </section>

    <section class="stack">
      <p class="eyebrow">All eight</p>
      <router-link
        v-for="lesson in lessons"
        :key="lesson.id"
        :to="`/lesson/${lesson.id}`"
        class="card lesson-row"
        :class="{ 'lesson-row--done': progress[lesson.id]?.cookedAt, 'lesson-row--current': next && next.id === lesson.id }"
      >
        <span class="lesson-row__number">{{ lesson.number }}</span>
        <span class="lesson-row__text">
          <span class="lesson-row__title">{{ lesson.title }}</span>
          <span class="lesson-row__dish muted">{{ lesson.dish }}</span>
        </span>
        <span v-if="progress[lesson.id]?.cookedAt" class="pill pill--green">cooked</span>
        <span v-else-if="progress[lesson.id]?.plannedFor" class="pill">{{ dayLabel(progress[lesson.id].plannedFor) }}</span>
      </router-link>
    </section>
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue';
import { LESSONS } from '../curriculum/lessons.js';
import { loadProgress, loadWeek, debriefHistory } from '../utils/course.js';
import { planningWeek, dayLabel, weekdayLabel } from '../utils/week.js';
import { setActivity } from '../state.js';

export default {
  name: 'HomeScreen',
  props: { hat: { type: String, default: null } },
  setup (props) {
    const progress = ref({});
    const weekEntry = ref(null);
    const week = planningWeek();
    setActivity('home');

    watch(() => props.hat, async (hat) => {
      if (!hat) { progress.value = {}; weekEntry.value = null; return; }
      const [p, w] = await Promise.all([loadProgress(hat), loadWeek(hat, week.key)]);
      progress.value = p;
      weekEntry.value = w;
    }, { immediate: true });

    const planned = computed(() => Object.entries(weekEntry.value?.lessons || {})
      .map(([id, iso]) => ({ lesson: LESSONS.find((l) => l.id === id), iso }))
      .filter((p) => p.lesson)
      .sort((a, b) => a.iso.localeCompare(b.iso)));
    // The next night to cook: the earliest planned lesson not yet cooked.
    const next = computed(() => planned.value.find((p) => !progress.value[p.lesson.id]?.cookedAt)?.lesson || null);
    const remaining = computed(() => LESSONS.filter((l) => !progress.value[l.id]?.cookedAt));
    const countOptions = computed(() => Array.from({ length: Math.min(5, remaining.value.length) }, (_, i) => i + 1));
    const lastDebrief = computed(() => debriefHistory(progress.value, LESSONS).slice(-1)[0] || null);

    return { lessons: LESSONS, progress, weekEntry, week, planned, next, remaining, countOptions, lastDebrief, dayLabel, weekdayLabel };
  }
};
</script>

<style scoped>
.small { font-size: 0.85rem; }
.hero { border-color: var(--accent); }
.hero__title { font-size: 1.8rem; }
.hero__actions { margin-top: 0.75rem; }
.count-btn { min-width: 3.6rem; font-size: 1.3rem; }
.night { display: flex; align-items: center; gap: 0.9rem; padding: 0.55rem 0; border-bottom: 1px solid var(--line); }
.night:last-of-type { border-bottom: 0; }
.night--done { opacity: 0.6; }
.night__day { width: 6.5rem; font-weight: 700; flex: 0 0 auto; }
.night--next .night__day { color: var(--accent); }
.night__text { display: flex; flex-direction: column; flex: 1; min-width: 0; }
.night__title { color: var(--text); font-weight: 700; text-decoration: none; }
.week__list { margin: 0; padding-left: 1.2rem; display: flex; flex-direction: column; gap: 0.4rem; }
.debrief-verdict { font-size: 1.05rem; }
.lesson-row { display: flex; align-items: center; gap: 0.9rem; text-decoration: none; color: inherit; }
.lesson-row--current { border-color: var(--accent); }
.lesson-row--done { opacity: 0.75; }
.lesson-row__number { width: 2rem; height: 2rem; border-radius: 50%; background: var(--accent-soft); color: var(--accent); display: inline-flex; align-items: center; justify-content: center; font-weight: 800; flex: 0 0 auto; }
.lesson-row--done .lesson-row__number { background: var(--green-soft); color: var(--green); }
.lesson-row__text { display: flex; flex-direction: column; flex: 1; min-width: 0; }
.lesson-row__title { font-weight: 700; }
.lesson-row__dish { font-size: 0.9rem; }
</style>
